import { ALTURA, ESTADOS, LARGURA, projetar } from './brasil';

/**
 * Cálculos do bloco Mapa do Brasil, sem React: ler "Cidade (UF)", achar a
 * sede do município, pôr o ponto no quadro, afastar o que se cobre e escrever
 * a leitura do mapa em texto.
 */

export const CORES = {
  azul: '#0055a1',
  verde: '#1f6b2d',
  vermelho: '#c8161d',
  laranja: '#e87b1f',
  marinho: '#1b2a6b',
  amarelo: '#ffd300',
};
export const MARCADORES = ['cheio', 'vazado', 'quadrado', 'anel'];

const UFS = ESTADOS.map((e) => e.uf);
const NOME_DA_UF = Object.fromEntries(ESTADOS.map((e) => [e.uf, e.nome]));

/** Nome sem acento, minúsculo, com espaço simples (a chave do municipios.json). */
export const chave = (texto) =>
  (texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** "Porto Velho (RO)", "Porto Velho/RO" ou "Porto Velho - RO". */
export const lerCidade = (texto) => {
  const m = (texto || '')
    .trim()
    .match(/^(.+?)\s*(?:\(\s*([A-Za-z]{2})\s*\)|[/–-]\s*([A-Za-z]{2}))\s*$/);
  if (!m) return null;
  const uf = (m[2] || m[3]).toUpperCase();
  return UFS.includes(uf) ? { nome: m[1].trim(), uf } : null;
};

/** [longitude, latitude] da sede do município, ou null. */
export const coordenadas = (tabela, cidade) => {
  const c = lerCidade(cidade);
  return (c && tabela?.[c.uf]?.[chave(c.nome)]) || null;
};

/** Posição em % do quadro (left, top). */
export const posicao = (lon, lat) => {
  const [x, y] = projetar(lon, lat);
  return [(x / LARGURA) * 100, (y / ALTURA) * 100];
};

/**
 * Separa os itens que se cobrem (cada um com x, y e raio r), pela linha que
 * liga os centros; o maior se mexe menos. A mesma entrada dá a mesma saída.
 */
export const afastar = (itens, folga = 1, voltas = 120) => {
  const p = itens.map((i) => ({ ...i }));
  for (let v = 0; v < voltas; v++) {
    let mexeu = false;
    for (let a = 0; a < p.length; a++) {
      for (let b = a + 1; b < p.length; b++) {
        const A = p[a];
        const B = p[b];
        let dx = B.x - A.x;
        let dy = B.y - A.y;
        let d = Math.hypot(dx, dy);
        const min = A.r + B.r + folga;
        if (d >= min) continue;
        if (d < 1e-6) {
          dx = 1;
          dy = 0;
          d = 1;
        }
        const falta = min - d;
        const wa = (B.r * B.r) / (A.r * A.r + B.r * B.r);
        const wb = 1 - wa;
        A.x -= (dx / d) * falta * wa;
        A.y -= (dy / d) * falta * wa;
        B.x += (dx / d) * falta * wb;
        B.y += (dy / d) * falta * wb;
        mexeu = true;
      }
    }
    if (!mexeu) break;
  }
  return p;
};

/** Categoria de cada ponto; sem categoria cadastrada, uma só, sem nome. */
export const categoriasDe = (data) => {
  const lista = (data.categorias || []).filter(Boolean);
  const padrao = data.fundo === 'azul' ? 'amarelo' : 'azul';
  const cats = (lista.length ? lista : [{ '@id': 'padrao' }]).map((c) => ({
    id: c['@id'],
    nome: (c.nome || '').trim(),
    marcador: MARCADORES.includes(c.marcador) ? c.marcador : 'cheio',
    cor: CORES[c.cor] ? c.cor : padrao,
  }));
  return cats;
};

/** Pontos com posição (os sem coordenadas ficam de fora) e categoria. */
export const pontosDe = (data) => {
  const cats = categoriasDe(data);
  return (data.pontos || [])
    .filter((p) => p && Number.isFinite(p.lon) && Number.isFinite(p.lat))
    .map((p, i) => {
      const [x, y] = posicao(p.lon, p.lat);
      const cat = cats.find((c) => c.id === p.categoria) || cats[0];
      return {
        id: p['@id'] || `p${i}`,
        n: i + 1,
        x,
        y,
        cidade: (p.cidade || '').trim(),
        nome: (lerCidade(p.cidade)?.nome || p.cidade || '').trim(),
        detalhe: (p.detalhe || '').trim(),
        link: (p.link || '').trim(),
        rotulo: !!p.rotulo,
        cat,
      };
    });
};

/**
 * Círculos de área proporcional ao valor, no centro de cada estado, já
 * afastados uns dos outros (em unidades do quadro: não depende da tela).
 * O maior tem `maximo` % da largura.
 */
export const circulosDe = (data, maximo = 9) => {
  const vals = (data.valores || []).filter(
    (v) => v && NOME_DA_UF[v.uf] && Number(v.valor) > 0,
  );
  if (!vals.length) return [];
  const vmax = Math.max(...vals.map((v) => Number(v.valor)));
  const base = vals.map((v) => {
    const e = ESTADOS.find((x) => x.uf === v.uf);
    const diam = maximo * Math.sqrt(Number(v.valor) / vmax);
    return {
      id: v['@id'] || v.uf,
      uf: v.uf,
      nome: NOME_DA_UF[v.uf],
      valor: Number(v.valor),
      d: diam,
      x: e.c[0],
      y: e.c[1],
      r: ((diam / 100) * LARGURA) / 2,
    };
  });
  return afastar(base, 4)
    .map((c) => ({
      ...c,
      x: (c.x / LARGURA) * 100,
      y: (c.y / ALTURA) * 100,
    }))
    .sort((a, b) => b.valor - a.valor);
};

const juntar = (itens) =>
  itens.length > 1
    ? `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`
    : itens[0] || '';

export const unidadeDe = (data, n) =>
  n === 1 && (data.unidadeSingular || '').trim()
    ? data.unidadeSingular.trim()
    : (data.unidade || '').trim();

/** A leitura do mapa em texto corrido, para quem não vê o desenho. */
export const descricao = (data) => {
  const titulo = (data.titulo || '').trim() || 'Mapa do Brasil';
  if (data.modo === 'estados') {
    const cs = circulosDe(data);
    if (!cs.length) return titulo;
    return `${titulo}. ${cs
      .map((c) => `${c.nome}: ${c.valor} ${unidadeDe(data, c.valor)}`.trim())
      .join('; ')}.`;
  }
  const pts = pontosDe(data);
  if (!pts.length) return titulo;
  const cats = categoriasDe(data);
  const partes = cats
    .map((c) => {
      const deles = pts.filter((p) => p.cat.id === c.id).map((p) => p.cidade);
      if (!deles.length) return null;
      return c.nome
        ? `${c.nome} (${deles.length}): ${juntar(deles)}`
        : juntar(deles);
    })
    .filter(Boolean);
  return `${titulo}. ${partes.join('. ')}.`;
};
